import dayjs, { Dayjs } from 'dayjs';
import type { IDisplaySchedule } from './mapSchedules';
import type { IPublishHistoryItem } from 'src/api/getPublishHistory';

export type PublishItem = IDisplaySchedule | IPublishHistoryItem;

export interface IScheduleWeek<T extends PublishItem = PublishItem> {
  key: string;
  title: string;
  items: T[];
}

export const THIS_WEEK_TITLE = 'This week';
export const NEXT_WEEK_TITLE = 'Next week';

export const getStartOfWeek = (date: Dayjs): Dayjs => {
  const day = date.day(); // 0=Sun, 1=Mon, ..., 6=Sat
  return date.subtract(day === 0 ? 6 : day - 1, 'day').startOf('day');
};

const getDate = (item: PublishItem): string => ('scheduled_for' in item ? item.scheduled_for : item.created_at);

export const groupPublishingByWeek = <T extends PublishItem>(
  items: T[],
  sortOrder: 'asc' | 'desc' = 'asc',
): IScheduleWeek<T>[] => {
  const now = dayjs();
  const thisWeek = getStartOfWeek(now);
  const nextWeek = thisWeek.add(7, 'day');

  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const weekKey = getStartOfWeek(dayjs(getDate(item))).format('YYYY-MM-DD');
    const group = grouped.get(weekKey) ?? [];
    group.push(item);
    grouped.set(weekKey, group);
  }

  return [...grouped.entries()]
    .sort(([a], [b]) => (sortOrder === 'asc' ? a.localeCompare(b) : b.localeCompare(a)))
    .map(([key, weekItems]) => {
      const weekStart = dayjs(key);
      const getTitle = () => {
        if (weekStart.isSame(thisWeek, 'day')) return THIS_WEEK_TITLE;
        if (weekStart.isSame(nextWeek, 'day')) return NEXT_WEEK_TITLE;

        return `W/C ${weekStart.format('DD/MM/YYYY')}`;
      };
      return { key, title: getTitle(), items: weekItems };
    });
};
